import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { Dialog, DialogContent, Stack } from '@mui/material';

import { InternalAffairsApi } from '@/api/api.internal-affairs';
import { DataGrid, MasterNameAutocomplete, NoResultItem } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { Button } from '@/components/TailwindComponents';
import { LocalStoragePrefix } from '@/constants';
import { MasterAddNameDetails } from '@/layouts/TabLayout/MasterNames/components/MasterAddNameDetails';
import { IMasterNamePerson, IMasterName } from '@/models';
import MasterIndexPersonBase from '@/pages/MasterIndex/Persons/MasterIndexPersonBase';
import { PeopleInvolvedTableColumns } from '@/pages/Tools/InternalAffairs/data';
import { useToastStore, useUserStore, ConfirmTypes } from '@/store';
import { getLocalStorageItem } from '@/utils';

interface MenuState {
  menuEl: HTMLButtonElement | null;
  person?: IMasterNamePerson;
}

export const InternalAffairsPeopleList = () => {
  const { InternalAffairsId, ViewInternalAffairsId } = useParams<{
    InternalAffairsId?: string;
    ViewInternalAffairsId?: string;
  }>();

  const caseId = InternalAffairsId || ViewInternalAffairsId;

  const [selectedPerson, setSelectedPerson] =
    useState<IMasterNamePerson | null>(null);
  const [menuState, setMenuState] = useState<MenuState>({
    menuEl: null,
  });
  const menuRef = useRef<HTMLDivElement>(null);
  const { user } = useUserStore();
  const { updateToast } = useToastStore();

  // Check if we're in view or edit mode from URL
  const isViewMode = window.location.pathname.includes('/view/');

  const [refreshKey, setRefreshKey] = useState(0);
  const [showNewPersonForm, setShowNewPersonForm] = useState(false);
  const [linkedPersonIds, setLinkedPersonIds] = useState<string[]>([]);
  const [autocompleteKey, setAutocompleteKey] = useState(0);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        handleMenuClose();
      }
    };

    if (menuState.menuEl) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuState.menuEl]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: IMasterNamePerson,
  ) => {
    event.stopPropagation();
    setMenuState({
      menuEl: event.currentTarget,
      person: row,
    });
    setSelectedPerson(row);
  };

  const handleMenuClose = () => {
    setMenuState({ menuEl: null });
  };

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `peopleInvolved-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return PeopleInvolvedTableColumns;
      }

      return mergeColumnSettings(PeopleInvolvedTableColumns, savedSettings);
    },
  );

  const columns = useDataGridColumns({
    type: 'peopleInvolved',
    columnSettings,
    openMenu: handleMenuOpen,
  });

  // Fetch people involved from API
  const fetchPeople = useCallback(
    async (
      page: number,
      pageSize: number,
      filter: string,
      sortParam?: string,
    ) => {
      if (!caseId) {
        return {
          rows: [],
          totalCount: 0,
          hasNextPage: false,
        };
      }

      try {
        const { data } = await InternalAffairsApi.getPeopleInvolved(caseId, {
          page,
          limit: pageSize,
          filter,
          sort: sortParam,
        });

        // Data is already in Master Index Person format
        const rows = (data.results || []).map((person: IMasterNamePerson) => ({
          ...person,
          id: person._id,
        }));

        // Store the IDs for excluding in autocomplete
        setLinkedPersonIds(rows.map((r: IMasterNamePerson) => r._id as string));

        return {
          rows,
          totalCount: rows.length,
          hasNextPage: data.hasNextPage || false,
        };
      } catch (error) {
        console.error('Error fetching people involved:', error);
        updateToast({
          open: true,
          message: 'Failed to fetch people involved',
        });
        return {
          rows: [],
          totalCount: 0,
          hasNextPage: false,
        };
      }
    },
    [caseId, updateToast],
  );

  const handleRemove = async () => {
    if (!caseId || !selectedPerson?._id) return;

    try {
      await InternalAffairsApi.removePeopleInvolved(caseId, [
        selectedPerson._id as string,
      ]);
      updateToast({
        open: true,
        type: ConfirmTypes.SUCCESS,
        message: 'Person removed from case',
      });
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('Error removing person:', error);
      updateToast({
        open: true,
        message: 'Failed to remove person from case',
      });
    }
    handleMenuClose();
  };

  const handleView = () => {
    if (!selectedPerson?._id) return;
    // Open Master Index person view in new tab
    window.open(`/master-index/persons/${selectedPerson._id}/view`, '_blank');
    handleMenuClose();
  };

  // Show the new person form
  const handleShowNewPersonForm = () => {
    setShowNewPersonForm(true);
  };

  // Go back to the list view
  const handleGoBack = () => {
    setShowNewPersonForm(false);
  };

  // Link the created person to the IA case after the form submits
  const handleAfterSubmit = async (personId: string) => {
    if (!caseId) return;

    try {
      await InternalAffairsApi.addPeopleInvolved(caseId, [personId]);
      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      console.error('Error linking person to case:', error);
      updateToast({
        open: true,
        message: error.message || 'Failed to link person to case',
      });
    }
  };

  // Handle selecting an existing person from autocomplete
  const handleSelectExistingPerson = async (person: IMasterName) => {
    if (!caseId || !person?._id) return;

    try {
      await InternalAffairsApi.addPeopleInvolved(caseId, [person._id]);
      updateToast({
        open: true,
        type: ConfirmTypes.SUCCESS,
        message: 'Person added to case',
      });
      setRefreshKey((prev) => prev + 1);
      setAutocompleteKey((prev) => prev + 1);
    } catch (error: any) {
      console.error('Error adding person:', error);
      updateToast({
        open: true,
        message: error.message || 'Failed to add person to case',
      });
    }
  };

  // Show message if no case ID (new case not yet saved)
  if (!caseId) {
    return (
      <div className="pt-6 text-center text-gray-500">
        Please save the case first before adding people involved.
      </div>
    );
  }

  return (
    <>
      <Dialog
        open={showNewPersonForm}
        onClose={handleGoBack}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh', maxHeight: '90vh' },
        }}
      >
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {showNewPersonForm && (
            <MasterIndexPersonBase>
              <MasterAddNameDetails
                onGoBack={handleGoBack}
                hideHeader={false}
                isMasterIndex={true}
                onAfterSubmit={handleAfterSubmit}
              />
            </MasterIndexPersonBase>
          )}
        </DialogContent>
      </Dialog>

      <div className="pt-3">
        <DataGrid
          className="w-full"
          key={refreshKey}
          name="peopleInvolved"
          autoPageSize={false}
          fetchData={fetchPeople}
          columns={columns}
          columnSettings={columnSettings}
          sx={{
            width: 'calc(100% - 20px)',
            minHeight: 300,
          }}
          disableMultipleColumnsSorting={true}
          setColumnSettings={setColumnSettings}
          getRowId={(row) => row?._id || row?.id || ''}
          hideQuickFilter
          cta={
            !isViewMode ? (
              <Stack
                flexDirection="row"
                alignItems="center"
                justifyContent="center"
                width="838px"
                gap="8px"
              >
                <MasterNameAutocomplete
                  key={autocompleteKey}
                  excludedMasterNames={linkedPersonIds.map(
                    (id) => ({ _id: id } as IMasterNamePerson),
                  )}
                  onChange={handleSelectExistingPerson}
                  noResults={
                    <NoResultItem
                      flexDirection="row"
                      alignItems="center"
                      sx={{ ml: -1 }}
                    >
                      <Button
                        onClick={handleShowNewPersonForm}
                        variant="link"
                        className="text-red-600"
                      >
                        + Add new
                      </Button>
                    </NoResultItem>
                  }
                />
                <Button
                  variant="default"
                  className="rounded-md px-[20px] whitespace-nowrap"
                  onClick={handleShowNewPersonForm}
                >
                  Add New
                </Button>
              </Stack>
            ) : undefined
          }
        />
      </div>

      {/* Actions menu */}
      {menuState.menuEl && (
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            left: menuState.menuEl?.getBoundingClientRect().left,
            top: menuState.menuEl?.getBoundingClientRect().bottom,
            zIndex: 1000,
          }}
        >
          <div className="bg-white dark:bg-black border border-gray-200 rounded-md shadow-lg min-w-[120px]">
            <button
              onClick={handleView}
              className="w-full text-left px-3 py-2 text-sm font-semibold hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
            >
              View
            </button>
            {!isViewMode && (
              <button
                onClick={handleRemove}
                className="w-full text-left px-3 py-2 text-sm font-semibold text-red-600 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};
